import { useState, useEffect } from 'react';
import { supabase } from '../admin-app/supabase';
import { findEligibilityDiscount, isEligibilityDiscountSubtype } from '../utils/eligibilityDiscounts';

interface LineItemConfig {
  id: string;
  name: string;
  type: string;
  description?: string;
  subtype?: string;
}

interface ProgramLineItem {
  id?: string;
  line_item_config_id: string;
  amount: number;
  is_optional: boolean;
  line_item_config?: LineItemConfig;
}

interface ProgramLineItemSelectorProps {
  lineItems: ProgramLineItem[];
  onChange: (items: ProgramLineItem[]) => void;
  billingType?: string;
  /** The program's brand: the Foster City Resident discount is offered on Kaleido programs only. */
  brandKey?: string;
}

/**
 * Can this eligibility discount be added? Only where `create_invoice_on_enrollment()` bills it per
 * student (the monthly and flex generators do not check eligibility), and only one eligibility
 * discount per program, since every price display shows a single discounted price.
 */
function canAddEligibilityDiscount(
  subtype: string | undefined,
  billingType: string | undefined,
  brandKey: string | undefined,
  hasEligibilityDiscount: boolean
): boolean {
  if (!isEligibilityDiscountSubtype(subtype)) return true;
  if (hasEligibilityDiscount) return false;
  if (subtype === 'bhcs_student') return billingType === 'fixed_term';
  // Kaleido only; the database refuses it on any other program's pricing.
  return brandKey === 'kaleido' && (billingType === 'fixed_term' || billingType === 'per_day');
}

export function ProgramLineItemSelector({ lineItems, onChange, billingType, brandKey }: ProgramLineItemSelectorProps) {
  const [availableConfigs, setAvailableConfigs] = useState<LineItemConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);

  useEffect(() => {
    fetchAvailableConfigs();
  }, []);

  async function fetchAvailableConfigs() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('line_item_configs')
        .select('*')
        .order('name');

      if (error) throw error;
      setAvailableConfigs(data || []);
      setLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching line item configs:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
      setLoading(false);
    }
  }

  // Hydrate line items with config if missing
  const hydratedLineItems = lineItems.map(item => {
    if (!item.line_item_config) {
      const config = availableConfigs.find(c => c.id === item.line_item_config_id);
      return { ...item, line_item_config: config };
    }
    return item;
  });

  function handleAddItem() {
    if (!selectedConfigId || amount === 0) return;

    const selectedConfig = availableConfigs.find(c => c.id === selectedConfigId);
    if (!selectedConfig) return;

    const newItem: ProgramLineItem = {
      line_item_config_id: selectedConfigId,
      amount,
      is_optional: false,
      line_item_config: selectedConfig
    };

    onChange([...lineItems, newItem]);
    setSelectedConfigId('');
    setAmount(0);
    setIsAdding(false);
  }

  function handleRemoveItem(index: number) {
    const updatedItems = lineItems.filter((_, i) => i !== index);
    onChange(updatedItems);
  }

  function isEligibilityDiscount(item: ProgramLineItem): boolean {
    return isEligibilityDiscountSubtype(item.line_item_config?.subtype);
  }

  const eligibilityDiscount = findEligibilityDiscount(hydratedLineItems.map(item => item.line_item_config?.subtype));
  // Left on a program whose brand was switched away from Kaleido: saving would be refused.
  const fosterCityOffBrand = eligibilityDiscount?.subtype === 'foster_city_resident' && brandKey !== 'kaleido';

  // The program's stored price is the REGULAR price and excludes the eligibility
  // discount — the discounted price is derived from it downstream. Summing the discount in
  // here would show a "Total" that never matches the price on the program card.
  function calculateTotal() {
    return hydratedLineItems
      .filter(item => !isEligibilityDiscount(item))
      .reduce((sum, item) => sum + item.amount, 0);
  }

  /** e.g. "BHCS: $675.00", or null without an eligibility discount. */
  function discountedTotalLabel(): string | null {
    const discount = hydratedLineItems
      .filter(isEligibilityDiscount)
      .reduce((sum, item) => sum + item.amount, 0);
    if (discount === 0 || !eligibilityDiscount) return null;
    return `${eligibilityDiscount.shortLabel}: $${(calculateTotal() + discount).toFixed(2)}`;
  }

  function handleToggleOptional(index: number, isOptional: boolean) {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], is_optional: isOptional };
    onChange(updatedItems);
  }

  function formatType(config?: LineItemConfig): string {
    if (!config?.type) return '-';
    return config.type.charAt(0).toUpperCase() + config.type.slice(1);
  }

  function typeRepeatsName(config?: LineItemConfig): boolean {
    if (!config?.type) return false;
    return config.type.toLowerCase() === config.name.toLowerCase();
  }

  function isLunchConfig(config?: LineItemConfig): boolean {
    if (!config) return false;
    const name = config.name.toLowerCase();
    const type = config.type.toLowerCase();
    const subtype = (config.subtype ?? '').toLowerCase();
    return name.includes('lunch') || type.includes('lunch') || subtype.includes('lunch');
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading line item configurations...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">Program Costs</h3>
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          + Add Cost Item
        </button>
      </div>

      {fosterCityOffBrand && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          The Foster City Resident discount is for Kaleido programs only. Remove it before saving.
        </div>
      )}

      {/* Line items */}
      {hydratedLineItems.length > 0 ? (
        <>
        {/* Mobile card list. Six columns cannot fit a phone at any wrapping, and
            this editor lives in a modal that is full-bleed below sm, so below md
            each item becomes a stacked card instead of a sideways-scrolling row. */}
        <div className="md:hidden space-y-2">
          {hydratedLineItems.map((item, index) => (
            <div key={index} className="rounded-md border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 break-words">
                    {item.line_item_config?.name || 'Loading...'}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {/* The table shows Name and Type in separate columns; stacked
                        together they read as a duplicate for items like "Tuition". */}
                    {!typeRepeatsName(item.line_item_config) && (
                      <span className="text-xs text-gray-500">{formatType(item.line_item_config)}</span>
                    )}
                    {isLunchConfig(item.line_item_config) && (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                        Lunch add-on
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  ${item.amount.toFixed(2)}
                </div>
              </div>

              {item.line_item_config?.description && (
                <p className="mt-2 text-sm text-gray-500 break-words">
                  {item.line_item_config.description}
                </p>
              )}

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={item.is_optional}
                    onChange={(e) => handleToggleOptional(index, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  Optional
                </label>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100 hover:text-red-900 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="flex items-baseline justify-between rounded-md border border-gray-200 bg-gray-50 p-3">
            <span className="text-sm font-medium text-gray-900">Total</span>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">${calculateTotal().toFixed(2)}</div>
              {discountedTotalLabel() !== null && (
                <div className="text-xs text-green-700">{discountedTotalLabel()}</div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                {/* Description absorbs the leftover width and wraps, so the short columns
                    after it stay on screen instead of being pushed off the right edge. */}
                <th scope="col" className="w-full px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Optional</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hydratedLineItems.map((item, index) => (
                <tr key={index}>
                  <td className="px-3 py-2 whitespace-nowrap align-top">
                    <div className="text-sm font-medium text-gray-900">
                      {item.line_item_config?.name || 'Loading...'}
                      {isLunchConfig(item.line_item_config) && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                          Lunch add-on
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap align-top">
                    <div className="text-sm text-gray-900">${item.amount.toFixed(2)}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap align-top">
                    <div className="text-sm text-gray-500">{formatType(item.line_item_config)}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="text-sm text-gray-500 break-words">{item.line_item_config?.description || '-'}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center align-top">
                    <input
                      type="checkbox"
                      checked={item.is_optional}
                      onChange={(e) => handleToggleOptional(index, e.target.checked)}
                      aria-label={`Optional: ${item.line_item_config?.name ?? 'cost item'}`}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium align-top">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-medium">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Total</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                  <div>${calculateTotal().toFixed(2)}</div>
                  {discountedTotalLabel() !== null && (
                    <div className="text-green-700 font-normal">{discountedTotalLabel()}</div>
                  )}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div className="text-sm text-gray-500 py-4 text-center border border-dashed rounded-md">
          No cost items added yet. Click "Add Cost Item" to start.
        </div>
      )}

      {/* Add new item form */}
      {isAdding && (
        <div className="mt-4 border p-4 rounded-md bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Add Cost Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Line Item</label>
              <select
                value={selectedConfigId}
                onChange={(e) => setSelectedConfigId(e.target.value)}
                className="w-full text-sm border rounded px-3 py-2"
                required
              >
                <option value="">Select a line item</option>
                {availableConfigs
                  .filter((config) =>
                    canAddEligibilityDiscount(config.subtype, billingType, brandKey, !!eligibilityDiscount))
                  .map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name} ({config.type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                className="w-full text-sm border rounded px-3 py-2"
                step="0.01"
                required
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="px-3 py-1 text-sm border rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddItem}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={!selectedConfigId || (amount === 0)}
            >
              Add Item
            </button>
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500">
        The total program price shown to clients is the sum of all cost items. A BHCS Students
        discount is not part of that total — it is applied on top, and only for qualifying students.
      </div>
    </div>
  );
} 